import { RiUploadCloud2Fill } from "@remixicon/react";
import { ErrorCode, useDropzone } from "react-dropzone";
import { DropzoneContainer, ThumbsContainer } from "./styled";
import { Spinner } from "reactstrap";
import { FileThumbs } from "./fileThumbs";
import { ISource, sourcesService } from "../../../services";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../redux/hooks";
import { actionDisplayNotification } from "../../../redux/notificationSlice";

interface DropzoneFileProps {
  loading: boolean;
  accessToDelete: boolean;
  accessToUpload: boolean;
  accessToDownload: boolean;
  sources: ISource[];
  onDrop: (acceptedFiles: File[]) => void;
  afterDelete: (file: ISource) => void;
  maxFileSize: number;
  deleteConfirmationMessage: string;
  inputHelperMessage: string;
}

export const HUNDRED_MB = 104_857_400;
export const TWENTY_FIVE_MB = 26_214_400;

export function DropzoneFile({
  loading,
  accessToDelete,
  accessToUpload,
  accessToDownload,
  maxFileSize,
  sources,
  onDrop,
  afterDelete,
  deleteConfirmationMessage,
  inputHelperMessage,
}: DropzoneFileProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  function formatErrorCode(filename: string, code: ErrorCode): string {
    if (code == ErrorCode.FileInvalidType) {
      return `Tipo de documento não suportado: ${filename}. Use PDF, Word, Excel ou Powerpoint.`;
    }
    if (code == ErrorCode.FileTooLarge) {
      const limit_in_mb = maxFileSize / 1_048_576;
      return `O documento ${filename} ultrapassa o limite de ${limit_in_mb.toFixed()}MB. Envie um documento menor.`;
    }
    return `Não foi possível processar o documento ${filename}. Tente novamente.`;
  }

  const { getRootProps, getInputProps } = useDropzone({
    disabled: !accessToUpload,
    multiple: true,
    maxSize: maxFileSize,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "text/plain": [".txt"],
      "text/csv": [".csv"],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
    onDrop: onDrop,
    onDropRejected(fileRejections) {
      fileRejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          dispatch(
            actionDisplayNotification({
              messages: [formatErrorCode(rejection.file.name, error.code as ErrorCode)],
              severity: "error",
            }),
          );
        });
      });
    },
  });

  async function handleDelete(file: ISource) {
    if (file.file_path) {
      try {
        await sourcesService.deleteSource({
          sourceId: file.id,
        });
      } catch (error) {
        dispatch(
          actionDisplayNotification({
            messages: [t("global.failureDeleteMessage")],
            severity: "error",
          }),
        );
      }
    }
    afterDelete(file);
    return;
  }

  async function downloadFile(file: ISource) {
    try {
      await sourcesService
        .getUploadUrl({
          sourceId: Number(file.id),
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.file_name;
          a.click();
        });
    } catch (er) {
      dispatch(
        actionDisplayNotification({
          messages: [t("global.failureDownloadMessage")],
          severity: "error",
        }),
      );
    }
  }

  return (
    <DropzoneContainer>
      <div {...getRootProps({ className: "dropzone" })}>
        {loading ? (
          <Spinner
            size="sm"
            className="button-icon flex-shrink-0 align-middle fs-16 me-2"
          ></Spinner>
        ) : (
          <>
            <input {...getInputProps()} disabled={!accessToUpload} />
            <RiUploadCloud2Fill size={53.2} className="drop-icon" />
            {accessToUpload ? (
              <p>{t("pages.projects.components.createEditPage.dropzone.permitted")}</p>
            ) : (
              <p className="text-warning">
                {t("pages.projects.components.createEditPage.dropzone.notPermitted")}
              </p>
            )}
          </>
        )}
      </div>
      <div className="form-text">{inputHelperMessage}</div>
      <ThumbsContainer>
        {sources?.map((file) => (
          <FileThumbs
            key={file.id}
            file={file}
            accessToDelete={!file.file_path ? true : accessToDelete}
            accessToDownload={!file.file_path ? true : accessToDownload}
            onDelete={handleDelete}
            onDownload={downloadFile}
            deleteDialogMessage={deleteConfirmationMessage}
          />
        ))}
      </ThumbsContainer>
    </DropzoneContainer>
  );
}
